import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import LogoImage from "assets/images/Logo blue.png";
import sampleJson from "../SurveyV4/sample.json";

import {
  Container,
  Content,
  TopBanner,
  BottomBar,
  ButtonWrapper,
  NextButton,
  BackButton,
  Progress,
  ProgressWrapper,
  AnimatedCard,
  ChatBarContainer,
  ChatBarInner,
  ChatInputField,
  SendBtn,
} from "../ActionPlan/styles";
import Loading from "../ActionPlan/Components/Loading";
import InvalidToken from "../ActionPlan/Components/InvalidToken";
import Completed from "../ActionPlan/Components/Completed";

import Introduction from "./Introduction";
import StatusStep from "./StatusStep";
import FollowUpQuestions from "./FollowUpQuestions";
import Review from "./Review";
import { SG_AP_BOOTSTRAP, SG_AP_GET_STATUS_OPTIONS, SG_AP_CHECKIN_SUBMIT } from "constants/actions";

const useActionPlanningState = () =>
  useSelector(
    (state) => state.actionPlanning || state.ap || state.action_plan || {},
    shallowEqual
  );

export default function CheckIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token: pathToken } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const queryToken = urlParams.get("token");
  const token = pathToken || queryToken || "";

  const apState = useActionPlanningState();
  const {
    loading: apLoading,
    loadingBootstrap: apLoadingBootstrap,
    submitting,
    submittingCheckin,
    error: apError,
    actions: apActions = [],
    activeEvent: apActiveEvent = null,
    statusOptions: apStatusOptions = [],
    invalidToken: apInvalidToken = false,
  } = apState;
  const apSubmitting = submitting || submittingCheckin;

  console.log(apActiveEvent)
  useEffect(() => {
    if (!token) return;
    dispatch({ type: SG_AP_BOOTSTRAP, payload: { token, kind: "update" } });
    dispatch({ type: SG_AP_GET_STATUS_OPTIONS, payload: { token } });
  }, [token, dispatch]);

  const [isFadingOut, setIsFadingOut] = useState(false);
  const [phase, setPhase] = useState(-1); // -1 intro, 0..n-1 status steps, n follow-ups, n+1 complete
  const [feedbackComplete, setFeedbackComplete] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitWarning, setSubmitWarning] = useState("");
  const [chatDraft, setChatDraft] = useState("");

  const themeColors = {
    buttonColor: sampleJson?.themeSettings?.buttonColor || "#007bff",
    highlightText: sampleJson?.themeSettings?.highlightText || "#333",
    topBanner: sampleJson?.themeSettings?.topBanner || "#f8f9fa",
    secondaryButton: sampleJson?.themeSettings?.secondaryButton || "#6c757d",
    backgroundColor: sampleJson?.themeSettings?.backgroundColor || "#ffffff",
  };

  // Debug/testing flags via URL params
  const forceLoadingParam = String((urlParams && urlParams.get("loading")) || "").toLowerCase();
  const forceLoading = forceLoadingParam === "1" || forceLoadingParam === "true";
  const forceCompletedParam = String((urlParams && urlParams.get("completed")) || "").toLowerCase();
  const forceCompleted = forceCompletedParam === "1" || forceCompletedParam === "true";

  // Loading gate with minimum duration + fade
  const [minLoadingElapsed, setMinLoadingElapsed] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [loadingLeaving, setLoadingLeaving] = useState(false);
  const loadingHideTimerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setMinLoadingElapsed(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const hasToken = Boolean(token);
    const wantsLoading = (hasToken ? apLoading : false) || forceLoading;
    if (wantsLoading) {
      if (!showLoading) setShowLoading(true);
      if (loadingLeaving) setLoadingLeaving(false);
    } else if (minLoadingElapsed) {
      if (showLoading && !loadingLeaving) {
        setLoadingLeaving(true);
      }
    }
  }, [apLoading, forceLoading, minLoadingElapsed, showLoading, loadingLeaving, token]);

  useEffect(() => {
    if (loadingLeaving && !loadingHideTimerRef.current) {
      loadingHideTimerRef.current = setTimeout(() => {
        setShowLoading(false);
        setLoadingLeaving(false);
        loadingHideTimerRef.current = null;
      }, 320);
    }
  }, [loadingLeaving]);

  useEffect(() => () => {
    if (loadingHideTimerRef.current) {
      clearTimeout(loadingHideTimerRef.current);
      loadingHideTimerRef.current = null;
    }
  }, []);

  const [actions, setActions] = useState([]);
  useEffect(() => {
    if (Array.isArray(apActions) && apActions.length > 0) {
      // Normalize fields coming from API
      setActions(apActions.map((a) => ({
        id: a.id,
        title: a.title || "",
        // preserve anything else you might need later
      })));
      return;
    }
    // Fallback to any locally saved plan
    try {
      const raw = localStorage.getItem("actionPlan:v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.actions)) {
          setActions(parsed.actions.map((a) => ({ id: a.id, title: a.title || "" })));
        }
      }
    } catch (e) {}
  }, [apActions]);

  // Statuses and notes for each action id
  const [statuses, setStatuses] = useState({});
  const [notes, setNotes] = useState({});

  function updateStatus(actionId, status) {
    setStatuses((prev) => ({ ...prev, [actionId]: status }));
  }
  function updateNote(actionId, note) {
    setNotes((prev) => ({ ...prev, [actionId]: note }));
  }

  const totalSteps = actions.length;
  const inFollowUps = phase >= actions.length && !feedbackComplete && !showReview;

  const followRef = useRef(null);

  function handleNext() {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setPhase((prev) => prev + 1);
    }, 300);
  }

  function handleBack() {
    if (phase <= -1) return;
    setIsFadingOut(true);
    setTimeout(() => {
      setIsFadingOut(false);
      setPhase((prev) => prev - 1);
    }, 300);
  }

  useEffect(() => {
    // When finishing, persist data
    if (feedbackComplete) {
      try {
        localStorage.setItem(
          "actionCheckIn:v1",
          JSON.stringify({
            actions: actions.map((a) => ({ id: a.id, title: a.title })),
            statuses,
            notes,
            completedAt: Date.now(),
          })
        );
      } catch (e) {}
    }
  }, [feedbackComplete, actions, statuses, notes]);

  const reportUrl = sampleJson?.themeSettings?.reportUrl;

  if (showLoading) {
    return <Loading themeColors={themeColors} leaving={loadingLeaving} />;
  }

  if (apInvalidToken && !apLoading) {
    return <InvalidToken themeColors={themeColors} />;
  }

  const isCheckInEvent = String(apActiveEvent?.event_type || "").toLowerCase() === "check_in";
  const checkInStatus = String(apActiveEvent?.response_status || apActiveEvent?.status || "").toLowerCase();
  const isCompletedFromApi = isCheckInEvent && (checkInStatus === "completed" || checkInStatus === "closed" || checkInStatus === "done");
  if (forceCompleted || isCompletedFromApi) {
    return <Completed themeColors={themeColors} />;
  }

  return (
    <Container themeColors={themeColors} style={{ fontFamily: "Geist Variable, sans-serif" }}>
      <TopBanner themeColors={themeColors}>
        <img src={LogoImage} alt="Logo" style={{ height: 30, position: "absolute", top: 20, left: 20 }} />
      </TopBanner>

      <BackgroundLayer>
        <BlobLeft />
        <BlobRight />
      </BackgroundLayer>

      <Foreground>
        <Content>
          <AnimatedCard isFadingOut={isFadingOut}>
            {!!apLoadingBootstrap && <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>Loading your check-in…</div>}
            {!!apSubmitting && <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>Submitting your check-in…</div>}
            {!!apError && !apLoading && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 8 }}>Couldn’t load server data; showing any saved local actions.</div>}
            {apActiveEvent && <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 6 }}>Event: {apActiveEvent.name || apActiveEvent.id}</div>}
            {feedbackComplete ? (
              <div>
                <h1 style={{
                  fontFamily: "Geist Variable, Geist, sans-serif",
                  fontWeight: 500,
                  fontSize: "clamp(30px, 6vw, 40px)",
                  margin: 0,
                  color: "oklch(.145 0 0)",
                }}>Check-In Complete</h1>
                <p style={{ marginTop: 8, color: "#4b5563", fontSize: 15 }}>Thanks for the update. Your statuses and feedback have been saved.</p>
              </div>
            ) : phase < 0 ? (
              <Introduction themeColors={themeColors} reportUrl={reportUrl} />
            ) : (phase >= actions.length && !feedbackComplete && showReview) ? (
              <Review
                themeColors={themeColors}
                actions={actions}
                statuses={statuses}
                notes={notes}
              />
            ) : inFollowUps ? (
              <FollowUpQuestions
                ref={followRef}
                themeColors={themeColors}
                actions={actions}
                statuses={statuses}
                notes={notes}
                onComplete={() => setShowReview(true)}
              />
            ) : (
              actions.length > 0 ? (
                <StatusStep
                  themeColors={themeColors}
                  isFadingOut={isFadingOut}
                  action={actions[Math.min(actions.length - 1, Math.max(0, phase))]}
                  index={Math.min(actions.length - 1, Math.max(0, phase))}
                  total={actions.length}
                  statusValue={statuses[actions[Math.min(actions.length - 1, Math.max(0, phase))]?.id]}
                  noteValue={notes[actions[Math.min(actions.length - 1, Math.max(0, phase))]?.id]}
                  onChangeStatus={updateStatus}
                  onChangeNote={updateNote}
                  statusOptions={apStatusOptions}
                />
              ) : (
                <div style={{ color: "#6b7280" }}>No actions found for this check-in.</div>
              )
            )}
          </AnimatedCard>
        </Content>
      </Foreground>

      <BottomBar>
        <ButtonWrapper>
          {(phase >= 0 || inFollowUps) && !feedbackComplete ? (
            <BackButton onClick={handleBack} themeColors={themeColors}>Back</BackButton>
          ) : (
            <div style={{ width: 100 }} />
          )}
        </ButtonWrapper>

        <ProgressWrapper>
          {phase >= 0 && !inFollowUps && !feedbackComplete ? (
            <Progress>
              Action {Math.min(actions.length, phase + 1)} of {actions.length}
            </Progress>
          ) : (
            <div style={{ height: 18 }} />
          )}
        </ProgressWrapper>

        <ButtonWrapper>
          {feedbackComplete ? (
            <div style={{ width: 100 }} />
          ) : phase < 0 ? (
            <NextButton onClick={() => handleNext()} active={true} themeColors={themeColors}>Start Check-In</NextButton>
          ) : !inFollowUps && !(phase >= actions.length && showReview) ? (
            phase < actions.length - 1 ? (
              <NextButton onClick={() => handleNext()} active={true} themeColors={themeColors}>Next</NextButton>
            ) : (
              <NextButton onClick={() => handleNext()} active={true} themeColors={themeColors}>Proceed to Follow-Up</NextButton>
            )
          ) : (phase >= actions.length && showReview) ? (
            <NextButton onClick={() => setShowSubmitConfirm(true)} active={true} themeColors={themeColors}>Submit Check-In</NextButton>
          ) : (
            <div style={{ width: 100 }} />
          )}
        </ButtonWrapper>
      </BottomBar>

      {inFollowUps && !feedbackComplete && !showReview && (
        <ChatBarContainer>
          <ChatBarInner>
            <ChatInputField
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              placeholder="Type your answer..."
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  const value = chatDraft;
                  if (!value) return;
                  followRef.current?.submitAnswer?.(value);
                  setChatDraft("");
                  setIsFadingOut(true);
                  setTimeout(() => setIsFadingOut(false), 260);
                }
              }}
            />
            <SendBtn
              themeColors={themeColors}
              onClick={() => {
                const value = chatDraft;
                if (!value) return;
                followRef.current?.submitAnswer?.(value);
                setChatDraft("");
                setIsFadingOut(true);
                setTimeout(() => setIsFadingOut(false), 260);
              }}
              aria-label="Send"
            >
              ➤
            </SendBtn>
          </ChatBarInner>
        </ChatBarContainer>
      )}

      {showSubmitConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(0,0,0,0.12)', padding: 16 }}>
            <div style={{ fontWeight: 600, color: 'oklch(.145 0 0)', fontSize: 16 }}>Submit Check-In?</div>
            <p style={{ marginTop: 6, color: '#4b5563', fontSize: 14 }}>Are you sure you want to submit your check-in now? You can still edit later if needed.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              <button onClick={() => setShowSubmitConfirm(false)} style={{ height: 40, padding: '0 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#111827', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={() => {
                  setShowSubmitConfirm(false);
                  let updatesCount = 0;
                  // Submit per-action direct check-ins
                  if (token) {
                    actions.forEach((a) => {
                      const status_id = statuses[a.id];
                      const body = {
                        ...(status_id ? { status_id } : {}),
                        ...(notes[a.id] ? { notes: notes[a.id] } : {}),
                      };
                      if (Object.keys(body).length === 0) return; // skip empty
                      updatesCount += 1;
                      dispatch({
                        type: SG_AP_CHECKIN_SUBMIT,
                        payload: {
                          token,
                          actionId: a.id,
                          checkinData: body,
                          useDirect: true,
                          eventId: apActiveEvent?.id,
                        },
                      });
                    });
                  }
                  if (updatesCount === 0) {
                    setSubmitWarning('Please select a status or add a note for at least one action before submitting.');
                    setTimeout(() => setSubmitWarning(''), 2600);
                    return;
                  }
                  // Show completion UI immediately
                  setIsFadingOut(true);
                  setTimeout(() => {
                    setIsFadingOut(false);
                    setFeedbackComplete(true);
                  }, 300);
                }}
                style={{ height: 40, padding: '0 14px', borderRadius: 8, border: 'none', background: themeColors.buttonColor, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {Boolean(submitWarning) && (
        <div role="alert" aria-live="assertive" style={{ position: 'fixed', left: '50%', bottom: 92, transform: 'translateX(-50%)', background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: 10, padding: '10px 14px', fontSize: 14, zIndex: 2200 }}>
          {submitWarning}
        </div>
      )}
    </Container>
  );
}

// Background gradient blobs to match Action Plan
const floatY1 = keyframes`
  0% { transform: translateY(-6px); opacity: 0.9; }
  100% { transform: translateY(6px); opacity: 1; }
`;

const floatY2 = keyframes`
  0% { transform: translateY(8px); opacity: 0.9; }
  100% { transform: translateY(-8px); opacity: 1; }
`;

const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
  top: -96px;
  height: 320px;
  pointer-events: none;
  z-index: 0;
`;

const BlobLeft = styled.div`
  position: absolute;
  left: -8%;
  top: 0;
  width: 320px;
  height: 320px;
  border-radius: 9999px;
  background: rgba(99, 102, 241, 0.2);
  filter: blur(60px);
  animation: ${floatY1} 8s ease-in-out infinite alternate;
`;

const BlobRight = styled.div`
  position: absolute;
  right: -6%;
  top: 24px;
  width: 288px;
  height: 288px;
  border-radius: 9999px;
  background: rgba(34, 211, 238, 0.2);
  filter: blur(60px);
  animation: ${floatY2} 10s ease-in-out infinite alternate;
`;

const Foreground = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
